/**
 * Sidebar Navigation
 *
 * Collapsible left sidebar with 7 nav links, API status, and demo badge.
 *
 * Responsive behavior:
 * - Desktop (>=1024px): expanded by default, user can collapse to icon-only
 * - Tablet (768-1023px): collapsed by default
 * - Mobile (<768px): hidden, controlled by MobileTopBar hamburger
 *
 * Collapse state persisted in localStorage.
 */

import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";
import {
  Activity,
  Brain,
  ChevronLeft,
  ChevronRight,
  Eye,
  ExternalLink,
  HeartPulse,
  Layers,
  LayoutDashboard,
  Plug2,
  Radio,
  Route as RouteIcon,
  ScrollText,
  ShieldCheck,
  Wifi,
  WifiOff,
  Workflow,
  X,
} from "lucide-react";
import { useDataMode } from "../../providers/DataModeProvider";
import { useEventStream } from "../../hooks/useEventStream";
import { AuthStatusSection } from "./AuthStatusSection";
import { cn } from "../../lib/utils";

const NAV_ITEMS = [
  { path: "/", label: "Overview", icon: LayoutDashboard },
  { path: "/pipeline", label: "Pipeline", icon: Workflow },
  { path: "/security", label: "Security", icon: ShieldCheck },
  { path: "/health", label: "Health", icon: HeartPulse },
  { path: "/knowledge", label: "Knowledge", icon: Brain },
  { path: "/traces", label: "Traces", icon: RouteIcon },
  { path: "/infra", label: "Infra", icon: Layers },
  { path: "/audit", label: "Audit", icon: ScrollText },
  { path: "/integrations", label: "Integrations", icon: Plug2 },
] as const;

const STORAGE_KEY = "crucio-sidebar-collapsed";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(STORAGE_KEY);
    // Default collapsed on tablet-width screens
    if (stored === null) return window.innerWidth < 1024;
    return stored === "true";
  });

  const { apiReachable, badgeState } = useDataMode();
  const location = useLocation();
  const { eventCount } = useEventStream();

  // Track unread events: reset when user visits Security page
  const lastSeenCountRef = useRef(0);
  const unreadCount = eventCount - lastSeenCountRef.current;

  useEffect(() => {
    if (location.pathname === "/security") {
      lastSeenCountRef.current = eventCount;
    }
  }, [location.pathname, eventCount]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isExpanded = !collapsed || mobileOpen;

  return (
    <>
      {/* Mobile backdrop overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50",
          "flex flex-col shrink-0 h-full",
          "bg-bg-secondary border-r border-border",
          "transition-all duration-200 ease-in-out",
          // Mobile: slide in/out
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          // Width: expanded or collapsed
          isExpanded ? "w-56" : "w-14",
        )}
      >
        {/* Header: Logo + close (mobile) */}
        <div
          className={cn(
            "h-12 flex items-center shrink-0 border-b border-border",
            isExpanded ? "px-3 justify-between" : "px-0 justify-center",
          )}
        >
          {isExpanded ? (
            <Link
              to="/"
              className="flex items-center gap-2 group"
              onClick={onMobileClose}
            >
              <Activity className="w-5 h-5 text-accent-orange group-hover:text-accent-yellow transition-colors" />
              <span className="text-sm font-semibold text-text-primary">
                Crucio
              </span>
            </Link>
          ) : (
            <Link to="/" className="group">
              <Activity className="w-5 h-5 text-accent-orange group-hover:text-accent-yellow transition-colors" />
            </Link>
          )}

          {mobileOpen && (
            <button
              type="button"
              onClick={onMobileClose}
              className="lg:hidden p-1 text-text-muted hover:text-text-primary transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Navigation links */}
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
            const active = isActive(path);
            return (
              <Link
                key={path}
                to={path}
                onClick={onMobileClose}
                className={cn(
                  "relative flex items-center gap-3 rounded-md text-sm transition-colors",
                  isExpanded ? "px-3 py-2" : "justify-center px-2 py-2",
                  active
                    ? "bg-bg-hover text-accent-orange border-l-2 border-accent-orange -ml-px"
                    : "text-text-muted hover:text-text-primary hover:bg-bg-hover",
                )}
                title={!isExpanded ? label : undefined}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {isExpanded && <span className="flex-1">{label}</span>}
                {path === "/security" && unreadCount > 0 && (
                  <span
                    className={cn(
                      "flex items-center justify-center rounded-full bg-accent-red text-white font-medium",
                      isExpanded
                        ? "min-w-[18px] h-[18px] px-1 text-[10px]"
                        : "absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 text-[9px]",
                    )}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom section: status + collapse toggle */}
        <div className="border-t border-border p-2 space-y-1 shrink-0">
          {/* API status */}
          <div
            className={cn(
              "flex items-center text-xs py-1",
              isExpanded ? "gap-2 px-3" : "justify-center",
            )}
          >
            <span
              className={cn(
                "w-2 h-2 rounded-full shrink-0",
                apiReachable ? "bg-accent-green" : "bg-accent-red",
              )}
            />
            {isExpanded && (
              <span className="text-text-muted">
                {apiReachable ? "API Live" : "API Offline"}
              </span>
            )}
          </div>

          {/* Mode badge — reads canonical badgeState from DataModeProvider */}
          <div
            className={cn(
              "flex items-center text-xs py-1",
              isExpanded ? "gap-1.5 px-3" : "justify-center",
            )}
          >
            {badgeState === "live" ? (
              <>
                <Wifi className="w-3 h-3 text-accent-green shrink-0" />
                {isExpanded && (
                  <span className="text-accent-green font-medium">Live</span>
                )}
              </>
            ) : badgeState === "public" ? (
              <>
                <Eye className="w-3 h-3 text-accent-cyan shrink-0" />
                {isExpanded && (
                  <span className="text-accent-cyan font-medium">Public</span>
                )}
              </>
            ) : badgeState === "offline" ? (
              <>
                <WifiOff className="w-3 h-3 text-accent-yellow shrink-0" />
                {isExpanded && (
                  <span className="text-accent-yellow font-medium">
                    Offline
                  </span>
                )}
              </>
            ) : (
              <>
                <Radio className="w-3 h-3 text-accent-purple shrink-0" />
                {isExpanded && (
                  <span className="text-accent-purple font-medium">Demo</span>
                )}
              </>
            )}
          </div>

          {/* Auth — sign in / user profile */}
          <AuthStatusSection isExpanded={isExpanded} />

          {/* Author link + copyright */}
          <a
            href="https://brandonwie.dev"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center text-xs py-1 text-text-muted hover:text-accent-cyan transition-colors",
              isExpanded ? "gap-1.5 px-3" : "justify-center",
            )}
            title={!isExpanded ? "brandonwie.dev" : undefined}
          >
            <ExternalLink className="w-3 h-3 shrink-0" />
            {isExpanded && <span>brandonwie.dev</span>}
          </a>
          {isExpanded && (
            <div className="px-3 text-[10px] text-text-muted/60 leading-tight">
              © 2026 Seokhyun Wie (Brandon)
            </div>
          )}

          {/* Collapse toggle — desktop only */}
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "hidden lg:flex items-center w-full rounded-md text-xs",
              "text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors",
              isExpanded ? "gap-2 px-3 py-2" : "justify-center px-2 py-2",
            )}
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
